import merge from 'lodash/merge'
import PropTypes from 'prop-types'
import React, { useMemo, useState, useRef } from 'react'
import Field from 'redux-form/lib/Field'
import actions from 'redux-form/lib/actions'
import reduxForm from 'redux-form/lib/reduxForm'
import getFormSyncErrors from 'redux-form/lib/getFormSyncErrors'
import withRouter from 'react-router/lib/withRouter'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { omit } from 'lodash'

import {
  RowContainer,
  StyledOrderInfoForm,
  QuotationBox,
  StyledPlaceOrderButton,
} from './order-form.component.styles'
import {
  requiredAddressField,
  requirePackageSizeSeparateLWH,
  requireName,
  verifyPaymentOptions,
  requirePackageContent,
  charLimit100PackageContent,
  requirePackageWeightWithLimit,
} from './validation'
import { PickupDropoffForm } from './pickup-dropoff-form/pickup-dropoff-form.component'
import { InsuranceInfo } from './insurance-info/insurance-info.component'
import { ConnectedScheduleForm } from './schedule-form/schedule-form.component'
import { OrderDetailsForm } from './order-details-form/order-details-form.component'
import { ConnectedPaymentSummaryFormWithTranslation } from './payment-summary-form/payment-summary.component'
import { orderTrackingService } from '../tracking.service'
import { deliverySetOrderDetailImageUrl } from '../../duck/actions'
import { getDeliveryFormData } from '../../duck/selectors'
import { getHeaderBannerHeight } from 'ggx-global/duck/selectors'
import {
  getSavedCardInfo,
  getStripeCards,
} from 'ggx-src/payment/duck/selectors'
import getCountry from 'ggx-src/legacy/views/helpers/get_country'
import BET_3937 from 'ggx-src/feat/BET_3937'

export const DELIVERY_FORM_NAME = 'delivery-order'

const OrderForm = ({
  quotation,
  quotationLoading,
  quotationMeta,
  handleSubmit,
  orderInProgress,
  invalid,
  location = {},
  change,
  dispatch,
  headerBannerHeight,
  orderFormData,
  formSyncErrors,
  triggerUpdateSyncErrors,
  primaryStripeCard,
  stripeCards = [],
}) => {
  const [t] = useTranslation()
  const [currentForm, setCurrentForm] = useState('')

  // at current version of enzyme 3.10.0, shallow does not support useEffect hook
  // we use React.useEffect so it can be mocked in the test
  // https://blog.carbonfive.com/2019/08/05/shallow-testing-hooks-with-enzyme/
  React.useEffect(() => {
    const { state: orderData } = location
    if (orderData) {
      Object.keys(orderData).forEach(property => {
        dispatch(change(`${property}`, location.state[property]))
      })
    }

    return () => {
      dispatch(deliverySetOrderDetailImageUrl(null))
    }
  }, [])

  function handleResetAddressSyncErrors(target) {
    triggerUpdateSyncErrors({
      ...formSyncErrors,
      [target]: omit(formSyncErrors.shipper, [
        'address',
        'latitude',
        'longitude',
      ]),
    })
  }

  function handlePlaceOrderClick() {
    if (invalid) {
      orderTrackingService.trackPlaceOrder({ quotation })

      const firstErrorNode = document.querySelector('[data-error-field="true"]')

      if (firstErrorNode) {
        firstErrorNode.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }

    handleSubmit()
  }

  const handlePageClick = event => {
    if (orderFormData === undefined) return;
    // removing default values from orderFormData to check if there is any input by user
    const {
      hasCreditCard,
      hasSufficientBalance,
      payment_type,
      pickup_time,
      ...formData
    } = orderFormData
    const newFocusedForm = event
      .composedPath()
      .find(node => node.dataset?.testid?.includes('form'))
    const focusedFormName = newFocusedForm?.dataset?.testid

    if (Object.keys(formData).length === 0) return
    if (!focusedFormName || focusedFormName === currentForm) return
    if (focusedFormName !== currentForm) {
      setCurrentForm(focusedFormName)
      orderTrackingService.trackPlaceOrderFormEvents({
        quotation,
        formName: focusedFormName,
      })
    }
  }

  const onClickRef = useRef(handlePageClick)
  const onFocusRef = useRef(handlePageClick)

  React.useEffect(() => {
    onClickRef.current = handlePageClick
    onFocusRef.current = handlePageClick
  })

  React.useEffect(() => {
    // focus event listener is to track when user use tab to switch between forms
    const onClickHandler = e => onClickRef.current(e)
    const onFocusHandler = e => onFocusRef.current(e)

    globalThis.addEventListener('click', onClickHandler)
    globalThis.addEventListener('focus', onFocusHandler, true)

    return () => {
      globalThis.removeEventListener('click', onClickHandler)
      globalThis.removeEventListener('focus', onFocusHandler, true)
    }
  }, [])

  const IsSelectedCardExpired = React.useMemo(() => {
    if (getCountry() === 'HK' || getCountry() === 'SG') {
      if (orderFormData?.payment_type.startsWith('credit_card')) {
        const cardId = Number(
          orderFormData?.payment_type.replace('credit_card_', '')
        )
        const card =
          stripeCards.find(card => card.id === cardId) || primaryStripeCard
        return card.is_expired
      }
    }

    return false
  }, [orderFormData?.payment_type])

  const isDPMonthlySettlementEnabled = BET_3937.useIsDPMonthlySettlementEnabled()

  return (
    <RowContainer>
      <form onSubmit={handlePlaceOrderClick}>
        <StyledOrderInfoForm>
          <PickupDropoffForm
            dataTestid="pickup-form"
            handleResetAddressSyncErrors={handleResetAddressSyncErrors}
          />
          <OrderDetailsForm
            dataTestid="order-details-form"
            size_option={orderFormData?.size_option}
          />
          <ConnectedScheduleForm dataTestid="schedule-form" />
          <InsuranceInfo />
        </StyledOrderInfoForm>
      </form>
      <QuotationBox headerBannerHeight={headerBannerHeight}>
        {
          <Field
            name="payment_type"
            component={
              BET_3937.ConnectedPaymentSummaryFormWithTranslation ??
              ConnectedPaymentSummaryFormWithTranslation
            }
            quotation={quotation}
            quotationLoading={quotationLoading}
            quotationMeta={quotationMeta}
          />
        }
        <StyledPlaceOrderButton
          buttonType="primary"
          onClick={handlePlaceOrderClick}
          disabled={
            orderInProgress ||
            (!isDPMonthlySettlementEnabled &&
              (getCountry() === 'HK' || getCountry() === 'SG') &&
              ((orderFormData?.payment_type === 'credit_card' &&
                IsSelectedCardExpired) ||
                (orderFormData?.payment_type === 'prepaid_wallet' &&
                  !orderFormData?.hasSufficientBalance)))
          }
        >
          {t('orders.new.basicInfo.button.placeOrder')}
        </StyledPlaceOrderButton>
      </QuotationBox>
    </RowContainer>
  )
}

OrderForm.propTypes = {
  quotation: PropTypes.shape({
    total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    breakdown: PropTypes.array,
    discount: PropTypes.object,
  }),
  quotationMeta: PropTypes.shape({
    error: PropTypes.string,
  }),
  quotationLoading: PropTypes.bool,
  orderInProgress: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  invalid: PropTypes.bool,
  location: PropTypes.shape({
    state: PropTypes.object,
  }),
  change: PropTypes.func,
  dispatch: PropTypes.func,
  headerBannerHeight: PropTypes.string,
  orderFormData: PropTypes.object,
  formSyncErrors: PropTypes.shape({
    shipper: PropTypes.object,
  }),
  triggerUpdateSyncErrors: PropTypes.func.isRequired,
  primaryStripeCard: PropTypes.object,
  stripeCards: PropTypes.arrayOf(PropTypes.object),
}

const ReduxOrderForm = reduxForm({
  form: DELIVERY_FORM_NAME,
})(withRouter(OrderForm))

const ReduxOrderFormWithTranslation = props => {
  const [t] = useTranslation()

  const syncValidators = useMemo(() => {
    const addressErrorMessages = {
      invalidAddressMessage: t('error.invalid.address'),
      outOfServiceAddressMessage: t('error.invalid.addressOutOfService'),
    }

    const packageSizeValidator = requirePackageSizeSeparateLWH({
      invalidError: t('validation__lwh_empty'),
      overSizedError: t('validation__sum_lwh_exceeds', { total: '180' }),
      oneSideOverSizedError: t('validation__any_dimension_cannot_exceed', {
        singleSideDimension: '120',
      }),
    })

    const packageWeightValidator = requirePackageWeightWithLimit({
      invalidError: t('validation__weight_empty'),
      overWeightError: t('validation__weight_exceeds', { weight: '20' }),
    })

    return values => {
      const validators = [
        requireName(t('error.missing.name'), 'shipper'),
        requireName(t('error.missing.name'), 'recipient'),
        requiredAddressField(addressErrorMessages, 'shipper'),
        requiredAddressField(addressErrorMessages, 'recipient'),
        packageSizeValidator,
        packageWeightValidator,
        verifyPaymentOptions({
          cardError: 'incomplete_number',
          walletError: t('validation__wallet_balance_insufficient'),
        }),
        requirePackageContent(t('error.missing.package.content'), 'info'),
        charLimit100PackageContent(t('error.characterLimit100'), 'info'),
      ].filter(Boolean)
      return merge({}, ...validators.map(validator => validator(values)))
    }
  }, [t])

  return <ReduxOrderForm {...props} validate={syncValidators} />
}

const mapStateToProps = state => ({
  formSyncErrors: getFormSyncErrors(DELIVERY_FORM_NAME)(state),
  headerBannerHeight: getHeaderBannerHeight(state),
  orderFormData: getDeliveryFormData(state, DELIVERY_FORM_NAME),
  primaryStripeCard: getSavedCardInfo(state),
  stripeCards: getStripeCards(state) ?? [],
})

const mapDispatchToProps = dispatch => ({
  triggerUpdateSyncErrors: syncErrors => {
    dispatch(actions.updateSyncErrors(DELIVERY_FORM_NAME, syncErrors))
  },
})

const ConnectedReduxOrderFormWithTranslation = connect(
  mapStateToProps,
  mapDispatchToProps
)(ReduxOrderFormWithTranslation)

export {
  ConnectedReduxOrderFormWithTranslation,
  ReduxOrderFormWithTranslation,
  ReduxOrderForm,
}
